/**
 * @author        matthew@zumper.com (Matthew Talamini)
 * @copyright     Copyright (c) 2021, Zumper
 * @description   LocaleGuide selectors
 */

import { selectLocation } from '@zcode-ui/web-zumper-global-state'
import { CityType, NeighborhoodType } from '@zcode/enums'
import { oisEmpty } from '@zcode/nr-utils'
import type {
  FeaturesTypes,
  RentResearchUniqueBedroomsTypes,
  State,
  UniqueBedroomsTypes,
  VerticalPropertyTypes,
} from '@zcode/state-type'
import { compose } from 'redux'
import type { RentalDataFacet } from '../../helpers/getRentalDataForFacetAndPropertyType'
import { getNameString } from '../helpers'
export * from './router'
export * from './popularHoods'

export const selectGeos = (state: State) => state.geo
export const selectLocalLogicScores = (state: State) => state?.localLogic?.locationScores

export const selectLocaleGuideLocale = (state: State) => {
  const { city, hood } = state.currentSearch
  const { cityName, hoodName } = selectLocation(state) ?? {}

  // we aren't always guaranteed hood or city name in currentSearch location
  if (hood && (hoodName ?? hood.name)) {
    return hood
  }
  if (city && (cityName ?? city.name)) {
    return city
  }
  return null
}

const selectLocaleGuideCity = (state: State) => {
  const { city } = state.currentSearch
  const { cityName } = selectLocation(state) ?? {}

  if (city && (cityName ?? city.name)) {
    return city
  }
  return null
}

export const selectLocaleGuideHood = (state: State) => state?.currentSearch?.hood ?? null

export const selectHasLocaleGuide = (state: State) => {
  const locale = selectLocaleGuideLocale(state)
  return locale && !oisEmpty(locale)
}

export const selectCurrentSearchMainImageUrl = compose(
  (locale) => locale?.mainImageUrl,
  selectLocaleGuideLocale
)
export const selectHasMainImage = (state: State) => {
  return !!selectCurrentSearchMainImageUrl(state)
}

export const selectCurrentSearchPath = compose((locale) => locale?.path, selectLocaleGuideLocale)

export const selectCurrentSearchUrl = compose((locale) => locale?.url, selectLocaleGuideLocale)

export const selectCurrentSearchShortName = compose(
  (locale) => locale?.name,
  selectLocaleGuideLocale
)

export const selectCurrentCityFullName = compose(
  (locale) => [locale?.name, locale?.state].filter(Boolean).join(', '),
  selectLocaleGuideCity
)

export const selectCurrentSearchListingCount = compose(
  (locale) => locale?.listingCount,
  selectLocaleGuideLocale
)

export const selectCurrentSearchState = compose((locale) => locale?.state, selectLocaleGuideLocale)

export const selectCurrentSearchNeighborhoods = compose(
  (locale) => locale?.neighborhoods ?? [],
  selectLocaleGuideCity
)

export const selectCurrentSearchDemographics = (state: State) => {
  const locale = selectCurrentSearchIsHood(state)
    ? selectLocaleGuideHood(state)
    : selectLocaleGuideCity(state)
  return locale?.data?.demographics
}

export const selectCurrentSearchName = (state: State) => {
  const locale = selectLocaleGuideLocale(state)
  return getNameString(locale)
}

export const selectCurrentSearchIntro = compose(
  (locale) => locale?.html?.long_description?.intro,
  selectLocaleGuideLocale
)

export const selectCurrentSearchContent = compose(
  (locale) => locale?.html?.long_description?.content,
  selectLocaleGuideLocale
)

interface IndexabilityParams {
  xfr: VerticalPropertyTypes
  vertical?: string
}

const selectIndexedHoods = (state: State, { xfr, vertical = '/' }: IndexabilityParams) => {
  const hoods = selectCurrentSearchNeighborhoods(state)
  return hoods
    .filter((hood) => {
      const verticals = hood?.data?.verticals?.[xfr]
      return verticals?.includes(vertical)
    })
    .map((hood) => {
      return {
        name: hood.name,
        listUrl: hood.listUrl,
        path: hood.path,
      }
    })
}

export const selectCurrentMinCities = (state: State) => {
  return state.currentSearch.minCities?.minCities
}

export const selectCurrentSearchFeatures = compose(
  (locale) => locale?.html?.features,
  selectLocaleGuideLocale
)

export const selectCurrentCityFeatures = compose(
  (locale) => locale?.html?.features,
  selectLocaleGuideCity
)

export const selectCurrentMinCity = compose(
  (minCities): (typeof minCities)[0] | undefined => minCities?.[0],
  selectCurrentMinCities
)

const selectCurrentSearch = (state: State) => state?.currentSearch

export const selectCityGuideCity = compose(
  (currentSearch) => currentSearch?.cityGuideCity?.data,
  selectCurrentSearch
)

export const selectCityGuideContent = compose(
  (data) => (data && 'content' in data ? data.content : undefined),
  selectCityGuideCity
)

export const selectCurrentCity = (state: State) => {
  return state?.currentSearch?.city ?? selectCurrentMinCity(state)
}

export const selectCityGuideNeighborhood = compose(
  (currentSearch) => currentSearch?.cityGuideNeighborhood?.data,
  selectCurrentSearch
)

export const selectCityGuideNeighborhoodContent = compose(
  (data) => (data && 'content' in data ? data.content : undefined),
  selectCityGuideNeighborhood
)

export const selectCityGuideCityPOIs = compose(
  (data) => (data && 'pois' in data ? data.pois : undefined),
  selectCityGuideCity
)

export const selectCityGuideNeighborhoodPOIs = compose(
  (data) => (data && 'pois' in data ? data.pois : undefined),
  selectCityGuideNeighborhood
)

export const selectCityGuideCityColiCategories = compose(
  (data) => (data && 'costOfLiving' in data ? data.costOfLiving?.categories : undefined),
  selectCityGuideCity
)

export const selectCityGuideNeighborhoodColiCategories = compose(
  (data) => (data && 'costOfLiving' in data ? data.costOfLiving?.categories : undefined),
  selectCityGuideNeighborhood
)

export const selectZumperGuideAdditionalSections = (state: State) => {
  return (
    selectCityGuideNeighborhoodContent(state)?.additional_sections ??
    selectCityGuideContent(state)?.additional_sections
  )
}

export const selectCurrentHood = (state: State) => {
  return state?.currentSearch?.hood
}

export const selectPriceData = (state: State) => state?.priceData

export const selectPriceDataRecentAverages = compose(
  (priceData) => priceData?.recentAverages,
  selectPriceData
)

export const selectPriceDataYOYChange = compose(
  (priceData) => priceData?.yoyChange,
  selectPriceData
)

export const selectCurrentSearchIsHood = (state: State) => {
  const hood = selectCurrentHood(state)
  const { hoodName } = selectLocation(state) ?? {}

  if (!hood || !(hood.name || hoodName)) {
    return false
  }

  const city = selectCurrentCity(state)

  if (city?.type === CityType.UNIVERSITY) {
    return false
  }
  return hood.type !== NeighborhoodType.ZIPCODE
}

export const selectCurrentSearchHasHood = (state: State) => {
  const hood = selectCurrentHood(state)
  const { hoodName } = state?.currentSearch?.location ?? {}
  if (hood && (hood.name || hoodName)) {
    return true
  }

  return false
}

export * from './schoolWidget'

export const selectFaqData = (state: State) => state?.faqData?.data

export const selectFaqDataRentRangesGrouped = compose(
  (faqData) => faqData?.rentRangesGrouped,
  selectFaqData
)

export const selectFaqDataFeatures = compose((faqData) => faqData?.features, selectFaqData)
export const selectFaqDataBedrooms = compose((faqData) => faqData?.bedrooms, selectFaqData)

export const selectFaqDataBedroomByFacet = (state: State, facet?: string) => {
  const bedrooms = selectFaqDataBedrooms(state)
  const bedroomKey = facetToFaqDataBedroom(facet)
  if (bedroomKey && bedrooms) {
    return bedrooms[bedroomKey]
  }
}

export const facetToFaqDataBedroom = (
  facet?: string
): keyof Omit<UniqueBedroomsTypes, '_data'> | undefined => {
  if (!facet || facet === '/') {
    return
  }
  const slashRemovedFacet = facet.replace('/', '')
  switch (slashRemovedFacet) {
    case '1-beds':
      return 'oneBed'
    case '2-beds':
      return 'twoBed'
    case '3-beds':
      return 'threeBed'
    case '4+beds':
      return 'fourPlusBed'
    case 'studio':
      return 'studio'
  }
}

export const facetToRentResearchDataBedroom = (
  facet?: RentalDataFacet
): keyof RentResearchUniqueBedroomsTypes | undefined => {
  if (!facet || facet === '/') {
    return
  }
  const slashRemovedFacet = facet.replace('/', '')
  switch (slashRemovedFacet) {
    case '1-beds':
      return 'oneBeds'
    case '2-beds':
      return 'twoBeds'
    case '3-beds':
      return 'threeBeds'
    case '4+beds':
      return 'fourBeds'
    case 'studios':
      return 'studio'
  }
}

export const selectFaqDataFeatureByFacet = (state: State, facet?: string) => {
  const features = selectFaqDataFeatures(state)
  const featureKey = facetToFaqDataFeature(facet)

  if (featureKey && features) {
    return features[featureKey]
  }
}

export const facetToFaqDataFeature = (
  facet?: string
): keyof Omit<FeaturesTypes, '_data'> | undefined => {
  if (!facet || facet === '/') {
    return
  }

  const slashRemovedFacet = facet.replace('/', '')

  switch (slashRemovedFacet) {
    case 'furnished':
    case 'luxury':
    case 'cheap':
      return slashRemovedFacet
    case 'low-income':
      return 'lowIncome'
    case 'move-in-special':
      return 'moveInSpecial'
    case 'utilities-included':
      return 'utilitiesIncluded'
    case 'no-fee':
      return 'noFee'
    case 'pet-friendly':
      return 'petFriendly'
  }
}
